import "./DatesSection.css";

import { EditGreyIcon } from "../../../../../../../assets";
import { ProjectDatesConstants } from "../../../../../../../constants";
import { convertDateToStringDDMMYYYY } from "../../../../../../../utils";
import { Column, Divider, ReadonlyText, Row } from "../../../../../../../widget";
import { useDatesSection } from "./useDatesSection";

interface DatesSectionProps {
  handleEditDatesClick: () => void;
}

export const DatesSection = ({ handleEditDatesClick }: DatesSectionProps): JSX.Element => {
  const { dates, isReadOnly, dateSchema } = useDatesSection();

  return (
    <>
      <Row spacingV="ll" justify="between">
        <Column span={6}>
          <h2>Dates</h2>
        </Column>
        <Column span={2} className="editButtonColumn">
          {!isReadOnly && (
            <div className="editDatesButton">
              <EditGreyIcon onClick={() => handleEditDatesClick()} />
            </div>
          )}
        </Column>
      </Row>
      <Row spacingV="l">
        <Column span={4}>
          <ReadonlyText
            label="Registration date"
            value={
              dates?.design.registrationDate
                ? convertDateToStringDDMMYYYY(dates?.design.registrationDate)
                : "Not available"
            }
            noDataAvailable={!dates?.design.registrationDate}
            tooltipText={ProjectDatesConstants.DESIGN_REGISTRATION_DATE_TOOLTIP_TEXT}
            tooltipHeader=""
          />
        </Column>
        {dateSchema?.implementation.properties.implementationDate !== undefined && (
          <Column span={4} offset={1}>
            <div className="DatesSectionOptionalReadonlyTextContainer">
              <ReadonlyText
                label="Project implementation date"
                value={
                  dates?.implementation.implementationDate
                    ? convertDateToStringDDMMYYYY(dates?.implementation.implementationDate)
                    : "Not available"
                }
                noDataAvailable={!dates?.implementation.implementationDate}
                tooltipText={ProjectDatesConstants.IMPLEMENTATION_IMPLEMENTATION_DATE_TOOLTIP_TEXT}
                tooltipHeader=""
              />
            </div>
          </Column>
        )}
        {dateSchema?.implementation.properties.certificationDate !== undefined && (
          <Column span={4} offset={1}>
            <div className="DatesSectionOptionalReadonlyTextContainer">
              <ReadonlyText
                label="Certification date"
                value={
                  dates?.implementation?.certificationDate
                    ? convertDateToStringDDMMYYYY(dates?.implementation.certificationDate)
                    : "Not available"
                }
                tooltipText={ProjectDatesConstants.IMPLEMENTATION_CERTIFICATION_DATE_TOOLTIP_TEXT}
                tooltipHeader=""
                noDataAvailable={!dates?.implementation.certificationDate}
              />
            </div>
          </Column>
        )}
        {dateSchema?.implementation.properties.startDate !== undefined && (
          <Column
            offset={dateSchema?.implementation.properties.implementationDate !== undefined ? undefined : 1}
            span={4}
          >
            <div className="DatesSectionOptionalReadonlyTextContainer">
              <ReadonlyText
                label="Project start date"
                value={
                  dates?.implementation.startDate
                    ? convertDateToStringDDMMYYYY(dates?.implementation.startDate)
                    : "Not available"
                }
                noDataAvailable={!dates?.implementation.startDate}
                tooltipText={ProjectDatesConstants.IMPLEMENTATION_START_DATE_TOOLTIP_TEXT}
                tooltipHeader=""
              />
            </div>
          </Column>
        )}
        {dateSchema?.implementation.properties.restorationValidationDate !== undefined && (
          <Column span={4}>
            <div className="DatesSectionOptionalReadonlyTextContainer">
              <ReadonlyText
                label="Restoration validation date"
                value={
                  dates?.implementation?.restorationValidationDate
                    ? convertDateToStringDDMMYYYY(dates?.implementation.restorationValidationDate)
                    : "Not available"
                }
                noDataAvailable={!dates?.implementation.startDate}
                tooltipText={ProjectDatesConstants.IMPLEMENTATION_RESTORATION_VALIDATION_DATE_TOOLTIP_TEXT}
                tooltipHeader=""
              />
            </div>
          </Column>
        )}
        <Column
          offset={
            dateSchema?.implementation.properties.startDate !== undefined &&
            (dateSchema.implementation.properties.implementationDate !== undefined ||
              dateSchema.implementation.properties.restorationValidationDate !== undefined)
              ? 1
              : undefined
          }
          span={4}
        >
          <ReadonlyText
            label="Project duration"
            value={dates?.duration.duration ?? "Not available"}
            noDataAvailable={!dates?.duration.duration}
            tooltipText={ProjectDatesConstants.IMPLEMENTATION_DURATION_TOOLTIP_TEXT}
            tooltipHeader=""
          />
        </Column>
      </Row>
      <Row spacingV="xl">
        <Column span={12}>
          <Divider type="horizontal" />
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={6}>
          <h2>Monitoring</h2>
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={4}>
          <ReadonlyText
            label="Validation date"
            value={
              dates?.implementation.validationDate
                ? convertDateToStringDDMMYYYY(dates?.implementation.validationDate)
                : "Not available"
            }
            noDataAvailable={!dates?.implementation.validationDate}
            tooltipText={ProjectDatesConstants.IMPLEMENTATION_FIRST_ASSESSMENT_DATE_TOOLTIP_TEXT}
            tooltipHeader=""
          />
        </Column>
        <Column offset={1} span={4}>
          <ReadonlyText
            label="Second assessment date"
            value={
              dates?.ongoingMonitoring.cycles[0]?.assessmentDate
                ? convertDateToStringDDMMYYYY(dates?.ongoingMonitoring.cycles[0].assessmentDate)
                : "Not available"
            }
            noDataAvailable={!dates?.ongoingMonitoring.cycles[0]?.assessmentDate}
            tooltipText={ProjectDatesConstants.ONGOING_MONITORING_SECOND_ASSESSMENT_DATE_TOOLTIP_TEXT}
            tooltipHeader=""
          />
        </Column>
      </Row>
      <Row spacingV="xl">
        <Column span={4}>
          <ReadonlyText
            label="Final assessment date"
            value={
              dates?.ongoingMonitoring.finalAssessmentDate
                ? convertDateToStringDDMMYYYY(dates?.ongoingMonitoring.finalAssessmentDate)
                : "Not available"
            }
            noDataAvailable={!dates?.ongoingMonitoring.finalAssessmentDate}
            tooltipText={ProjectDatesConstants.ONGOING_MONITORING_FINAL_ASSESSMENT_DATE_TOOLTIP_TEXT}
            tooltipHeader=""
          />
        </Column>
        <Column offset={1} span={4}>
          <ReadonlyText
            label="Monitoring frequency"
            value={dates?.ongoingMonitoring.subsequentFrequency ?? "Not available"}
            noDataAvailable={!dates?.ongoingMonitoring.subsequentFrequency}
            tooltipText={ProjectDatesConstants.ONGOING_MONITORING_SUBSEQUENT_FREQUENCY_TOOLTIP_TEXT}
            tooltipHeader=""
          />
        </Column>
      </Row>
    </>
  );
};
